// Конвертер между нашим внутренним форматом эскиза (Zustand store) и
// форматом PlaneGCS (SketchPrimitive[]).
//
// Наш формат:
//   { kind: 'point',  id, x, y }
//   { kind: 'line',   id, p1: pointId, p2: pointId }
//   { kind: 'circle', id, cx, cy, r }
//
//   Constraint:
//   { kind: 'horizontal',    lineId }
//   { kind: 'vertical',      lineId }
//   { kind: 'parallel',      line1Id, line2Id }
//   { kind: 'perpendicular', line1Id, line2Id }
//   { kind: 'coincident',    p1Id, p2Id }
//   { kind: 'equal_length',  line1Id, line2Id }
//   { kind: 'distance',      p1Id, p2Id, value }
//   { kind: 'point_on_line', pointId, lineId }
//   { kind: 'tangent',       aId, bId }
//   { kind: 'concentric',    c1Id, c2Id }
//   { kind: 'symmetric',     p1Id, p2Id, axisLineId }
//   { kind: 'midpoint',      pointId, lineId }
//   ('fix' не хранится как constraint — это поле fixed:true на точках)
//
// PlaneGCS формат:
//   { id, type: 'point',  x, y, fixed }
//   { id, type: 'line',   p1_id, p2_id }
//   { id, type: 'circle', c_id, radius }
//   {     type: 'horizontal',          l_id }   (без id)
//   {     type: 'p2p_distance',        p1_id, p2_id, distance }
//   ... и т.д.
//
// Возвращает {primitives: SketchPrimitive[]} — то что можно отдавать в solver.

export function sketchToGcsPrimitives(sketch) {
    const out = [];
    if (!sketch) return out;

    const allPrims = sketch.primitives || [];
    const points = allPrims.filter((p) => p.kind === 'point');
    const lines = allPrims.filter((p) => p.kind === 'line');
    const circles = allPrims.filter((p) => p.kind === 'circle');
    const arcs = allPrims.filter((p) => p.kind === 'arc');
    const pointById = new Map(points.map((p) => [p.id, p]));
    const constraints = sketch.constraints || [];

    // 1. Точки — простые { id, type:'point', x, y, fixed:false }
    for (const p of points) {
        out.push({ id: p.id, type: 'point', x: p.x, y: p.y, fixed: !!p.fixed });
    }

    // 2. Линии — { id, type:'line', p1_id, p2_id }
    for (const l of lines) {
        out.push({ id: l.id, type: 'line', p1_id: l.p1, p2_id: l.p2 });
    }

    // 3. Окружности — нужны: точка-центр + сама окружность
    //    Создаём фейковую точку с id "<circleId>_c".
    //    Если окружность зафиксирована (fixed) — центр тоже fixed,
    //    чтобы её нельзя было сдвинуть решателем.
    for (const c of circles) {
        const cId = c.id + '_c';
        out.push({ id: cId, type: 'point', x: c.cx, y: c.cy, fixed: !!c.fixed });
        out.push({ id: c.id, type: 'circle', c_id: cId, radius: c.r });
    }

    // 4. Дуги — PlaneGCS SketchArc: точка-центр '<arcId>_c' + start/end точки
    //    (p1/p2 эскиза) + radius + start_angle/end_angle. Углы и радиус
    //    вычисляем из текущей геометрии; решатель их подгонит под ограничения.
    //    Без этого дуги не участвовали в решателе — центр и радиус «не ехали»
    //    за концами, дуга рвалась (аудит эскизов #4).
    for (const arc of arcs) {
        const p1 = pointById.get(arc.p1);
        const p2 = pointById.get(arc.p2);
        if (!p1 || !p2) continue;
        const cId = arc.id + '_c';
        const cx = arc.cx, cy = arc.cy;
        const radius = Math.hypot(p1.x - cx, p1.y - cy);
        const startAngle = Math.atan2(p1.y - cy, p1.x - cx);
        const endAngle = Math.atan2(p2.y - cy, p2.x - cx);
        out.push({ id: cId, type: 'point', x: cx, y: cy, fixed: !!arc.fixed });
        out.push({
            id: arc.id, type: 'arc',
            c_id: cId, radius,
            start_id: arc.p1, end_id: arc.p2,
            start_angle: startAngle, end_angle: endAngle,
        });
    }

    // 4. Constraints — точные имена типов из PlaneGCS:
    //    horizontal_l, vertical_l, parallel, perpendicular_ll,
    //    p2p_coincident, p2p_distance, equal_length, point_on_line_pl
    let cn = 0;
    for (const ct of constraints) {
        const cid = ct.id || `c_${cn++}`;
        switch (ct.kind) {
            case 'horizontal':
                out.push({ id: cid, type: 'horizontal_l', l_id: ct.lineId });
                break;
            case 'vertical':
                out.push({ id: cid, type: 'vertical_l', l_id: ct.lineId });
                break;
            case 'parallel':
                out.push({ id: cid, type: 'parallel', l1_id: ct.line1Id, l2_id: ct.line2Id });
                break;
            case 'perpendicular':
                out.push({ id: cid, type: 'perpendicular_ll', l1_id: ct.line1Id, l2_id: ct.line2Id });
                break;
            case 'coincident':
                out.push({ id: cid, type: 'p2p_coincident', p1_id: ct.p1Id, p2_id: ct.p2Id });
                break;
            case 'equal_length':
                out.push({ id: cid, type: 'equal_length', l1_id: ct.line1Id, l2_id: ct.line2Id });
                break;
            case 'distance':
                out.push({ id: cid, type: 'p2p_distance', p1_id: ct.p1Id, p2_id: ct.p2Id, distance: ct.value });
                break;
            case 'point_on_line':
                out.push({ id: cid, type: 'point_on_line_pl', p_id: ct.pointId, l_id: ct.lineId });
                break;
            case 'diameter':
                /*
                 * Размер диаметра окружности.
                 *
                 * 🔴 c_id — id САМОЙ ОКРУЖНОСТИ, а не её центра. Раньше здесь
                 * стоял `ct.circleId + '_c'` — id фейковой точки-центра (её
                 * создаёт блок «3. Окружности» выше). PlaneGCS отвечал на это
                 * «Expected null or instance of Circle, got an instance of
                 * Point», solve падал целиком, и ВСЕ размеры эскиза переставали
                 * применяться: окружность оставалась того размера, каким её
                 * протянули мышью. В уроке 2 из-за этого отверстие не совпадало
                 * с Ø20, сколько бы раз ребёнок ни вписывал число.
                 *
                 * Ср. 'tangent' ниже: там в c_id тоже идёт id окружности.
                 */
                out.push({
                    id: cid, type: 'circle_diameter',
                    c_id: ct.circleId, diameter: ct.value,
                });
                break;
            case 'tangent': {
                // Касание. Тип PlaneGCS зависит от пары объектов:
                //   линия+окружность → tangent_lc (l_id, c_id);
                //   окружность+окружность → tangent_cc (c1_id, c2_id).
                // ct.aId / ct.bId — id двух выбранных примитивов.
                const isLine = (id) => lines.some((l) => l.id === id);
                const isCircle = (id) => circles.some((c) => c.id === id);
                if (isLine(ct.aId) && isCircle(ct.bId)) {
                    out.push({ id: cid, type: 'tangent_lc',
                        l_id: ct.aId, c_id: ct.bId });
                } else if (isCircle(ct.aId) && isLine(ct.bId)) {
                    out.push({ id: cid, type: 'tangent_lc',
                        l_id: ct.bId, c_id: ct.aId });
                } else if (isCircle(ct.aId) && isCircle(ct.bId)) {
                    out.push({ id: cid, type: 'tangent_cc',
                        c1_id: ct.aId, c2_id: ct.bId });
                }
                break;
            }
            case 'concentric':
                // Концентричность — центры двух окружностей совпадают.
                // Центр окружности в формате PlaneGCS — это точка с
                // id '<circleId>_c' (создаётся выше в блоке окружностей).
                out.push({
                    id: cid, type: 'p2p_coincident',
                    p1_id: ct.c1Id + '_c', p2_id: ct.c2Id + '_c',
                });
                break;
            case 'symmetric':
                // Симметрия — две точки зеркальны относительно линии-оси.
                // p1Id/p2Id — id точек, axisLineId — id линии-оси.
                out.push({
                    id: cid, type: 'p2p_symmetric_ppl',
                    p1_id: ct.p1Id, p2_id: ct.p2Id, l_id: ct.axisLineId,
                });
                break;
            case 'midpoint':
                // Середина — точка ровно посередине отрезка. В PlaneGCS
                // нет прямого «point is midpoint», поэтому собираем из
                // двух ограничений: точка ЛЕЖИТ на линии и одновременно
                // на серединном перпендикуляре её концов. Пересечение
                // этих двух условий — единственная точка: середина.
                out.push({
                    id: cid + '_on', type: 'point_on_line_pl',
                    p_id: ct.pointId, l_id: ct.lineId,
                });
                out.push({
                    id: cid + '_pb', type: 'point_on_perp_bisector_pl',
                    p_id: ct.pointId, l_id: ct.lineId,
                });
                break;
            // 'fix' (фиксация) не транслируется в отдельное ограничение
            // PlaneGCS — он выставляет fixed:true на точках примитива,
            // а fixed уже учитывается в блоках точек/окружностей выше.
            default:
                // unknown
                break;
        }
    }

    return out;
}


// Обратное преобразование: после solver'а получаем массив с обновлёнными
// (x, y) — обновляем наши точки и центры окружностей.
export function applyGcsResultToSketch(sketch, gcsResult) {
    if (!sketch || !Array.isArray(gcsResult)) return sketch;
    const byId = new Map();
    for (const p of gcsResult) {
        byId.set(p.id, p);
    }

    const newPrimitives = (sketch.primitives || []).map((p) => {
        if (p.kind === 'point') {
            const updated = byId.get(p.id);
            if (updated && updated.type === 'point') {
                return { ...p, x: updated.x, y: updated.y };
            }
        }
        if (p.kind === 'circle') {
            // Центр имеет id <circleId>_c
            const updatedCenter = byId.get(p.id + '_c');
            const updatedCircle = byId.get(p.id);
            return {
                ...p,
                cx: updatedCenter ? updatedCenter.x : p.cx,
                cy: updatedCenter ? updatedCenter.y : p.cy,
                r: updatedCircle && typeof updatedCircle.radius === 'number'
                    ? updatedCircle.radius : p.r,
            };
        }
        if (p.kind === 'arc') {
            // Центр дуги — точка '<arcId>_c'. Концы (p1/p2) обновляются как
            // обычные точки выше. Обновляем центр дуги вслед за решателем.
            const updatedCenter = byId.get(p.id + '_c');
            return {
                ...p,
                cx: updatedCenter ? updatedCenter.x : p.cx,
                cy: updatedCenter ? updatedCenter.y : p.cy,
            };
        }
        return p;
    });

    return { ...sketch, primitives: newPrimitives };
}
